/**
 * Natalie telemetry: the Datadog global-context key for the Natalie event id, plus a pure resolver
 * that derives the event id from the current pathname. No React / no monitoring imports so the
 * resolver stays unit-testable in isolation and reusable from the Natalie Redux telemetry listener.
 *
 * The key is flat snake_case (`natalie_event_id`) to match the attribute `conversation-agent` binds
 * and the Natalie dashboards facet on (`@natalie_event_id`).
 */
import { matchPath } from 'react-router-dom';

import { NatalieRoutes } from 'pages/Natalie/routes';

/** Datadog global-context key set on RUM + Logs while a participant is in a Natalie session. */
export const NATALIE_EVENT_ID_CONTEXT_KEY = 'natalie_event_id';

/** Datadog global-context key marking the active Natalie session as `test` or `real`. */
export const NATALIE_SOURCE_CONTEXT_KEY = 'natalie_source';

/**
 * A Natalie event id is a UUID, so the id position is validated by **shape** rather than against a
 * list of path segments that are known not to be event ids.
 *
 * The `/natalie/:id` match below is deliberately loose — it takes whatever segment follows
 * `/natalie/` and calls it an event id, which is right for a participant URL but cannot by itself
 * tell an id from a word. So anything not UUID-shaped is not an event id and is not tagged. That
 * rejects every non-event segment structurally, including ones nobody has added yet, and needs no
 * maintenance as routes come and go.
 *
 * **Match the shape, never the version.** This is `8-4-4-4-12` hex and nothing more: no `4` pinned
 * in the third group, no `[89ab]` variant nibble. Two independent reasons, either sufficient:
 *
 * 1. `natalie-api` mints event ids of more than one version by design. Ordinary events get a v4
 *    from `randomUUID()`, while intake events get a **deterministic v5** derived from the
 *    community id (`handlers/request-intake.ts` sets the version nibble to 5 explicitly), so that
 *    the same community always derives the same id. Both are permanent, not legacy.
 * 2. That service validates ids with a regex identical to this one, character for character
 *    (`lambda/natalie-api/validation.ts`). Tightening here would put the frontend at odds with
 *    the service that issues the ids.
 *
 * Production bears this out: over a 30-day window, six distinct v5 ids (e.g.
 * `e242c553-9919-5af4-b22b-bbdcf2e33b16`) plus seeded ids shaped
 * `00000000-0000-0000-0000-00000000000N`. A v4-specific pattern rejects all ten, and the failure
 * is silent — `natalie_event_id` just goes missing and the Natalie dashboards lose those sessions
 * with nothing in the logs to explain it. Do not tighten this.
 */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Resolve the Natalie event id from a pathname, or `undefined` when the route should not be tagged.
 *
 * For both the authenticated flow (`/natalie/:id/...`) and the accountless flow
 * (`/natalie/:eventId/:inviteCode/...`) the event id is the first path segment after `/natalie`, so
 * a non-exact `/natalie/:id` match captures it for either — no need to enumerate every sub-route or
 * replicate PreApp's invite-code gate.
 */
export const resolveNatalieEventId = (pathname: string): string | undefined => {
  const id = matchPath<{ id?: string }>(pathname, { path: NatalieRoutes.Details })?.params.id;

  return id && UUID_SHAPE.test(id) ? id : undefined;
};
